import type { AllocationBreakdown, InvestmentDetail, InvestmentTransactionExpenses } from '@/types';

/**
 * Shared labels/helpers for investment movimentações, used by the position
 * drawer and the full /investments/:id page. EXPENSE_LABELS mirrors the
 * backend's ExpensesDto keys — keep them in sync in one place.
 */

// "Carregar mais": the next page's movements after the ones loaded, under the latest page's paging and holding.
export function appendInvestmentDetailPage(current: InvestmentDetail | null, next: InvestmentDetail): InvestmentDetail {
  if (!current) return next;
  return {
    ...next,
    transactions: { ...next.transactions, items: [...current.transactions.items, ...next.transactions.items] },
  };
}

export const TRANSACTION_TYPE_LABELS: Record<string, string> = {
  BUY: 'Compra',
  SELL: 'Venda',
  TAX: 'Imposto',
  TRANSFER: 'Transferência',
};

export const EXPENSE_LABELS: Record<string, string> = {
  serviceTax: 'ISS',
  brokerageFee: 'Corretagem',
  incomeTax: 'IRRF',
  other: 'Outros',
  tradingAssetsNoticeFee: 'Aviso de Negociação',
  maintenanceFee: 'Manutenção',
  settlementFee: 'Liquidação',
  clearingFee: 'Clearing',
  stockExchangeFee: 'Bolsa',
  custodyFee: 'Custódia',
  operatingFee: 'Operacional',
};

/** Asset-type tag tones, hue-matched to the allocation chart slices via the
 * categorical `--chart-*` tokens (design system §01.6 — brand/semantic hues are
 * never category colors): teal fixed income, blue variable income, orange
 * funds, purple real estate. The hue lives only in the background tint — the
 * raw chart hues read at ~3:1 over their own tints, below the 4.5:1 these
 * text-3xs tags need, so the foreground stays semantic. */
export function assetTagToneCls(key: keyof AllocationBreakdown): string {
  switch (key) {
    case 'fixedIncome':
      return 'bg-chart-5/15 text-text-primary';
    case 'variableIncome':
      return 'bg-chart-7/15 text-text-primary';
    case 'funds':
      return 'bg-chart-2/15 text-text-primary';
    case 'realEstate':
      return 'bg-chart-8/15 text-text-primary';
    default:
      return 'bg-muted text-text-secondary';
  }
}

// BUY/SELL carry signed-flow semantics (money in / money out); TRANSFER and
// unknown types are directionless and stay neutral.
export function getTypeBadgeClass(type: string): string {
  if (type === 'BUY') return 'text-success-text bg-success-surface';
  if (type === 'SELL') return 'text-error-text bg-error-surface';
  if (type === 'TAX') return 'text-warning-text bg-warning-surface';
  return 'text-text-secondary bg-muted';
}

export function formatPercent(value: number): string {
  const sign = value >= 0 ? '+' : '';
  return `${sign}${value.toFixed(2)}%`;
}

export function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

export function getNonZeroExpenses(expenses: InvestmentTransactionExpenses): { label: string; value: number }[] {
  return Object.entries(EXPENSE_LABELS)
    .map(([key, label]) => ({ label, value: expenses[key as keyof InvestmentTransactionExpenses] }))
    .filter((e): e is { label: string; value: number } => e.value != null && e.value !== 0);
}
